import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { EstadoAutenticacao, CredenciaisLoginLojista, RespostaAutenticacaoCentral } from '../tipos';
import { servicoAutenticacao } from '../servicos/servico.autenticacao';

/**
 * Fatia de autenticação do Redux — Login centralizado por lojista
 * Requisitos: 1.4, 1.5, 6.1, 6.2, 6.3, 6.4, 6.5
 *
 * - Estado de lojista, token, estabelecimentos e status de autenticação
 * - Thunk `realizarLogin` para autenticação via servidor central
 * - Thunk `restaurarSessao` para restaurar sessão do localStorage na inicialização
 * - Action `realizarLogout` para limpar sessão
 * - Detecção de token expirado durante sessão ativa
 */

// ============================================================
// Async Thunks
// ============================================================

/**
 * Thunk assíncrono para realizar login via servidor central.
 * Chama servicoAutenticacao.loginCentral e armazena resultado no estado.
 * Trata erros de rede, timeout e credenciais inválidas.
 */
export const realizarLogin = createAsyncThunk<
  RespostaAutenticacaoCentral,
  CredenciaisLoginLojista,
  { rejectValue: string }
>('autenticacao/realizarLogin', async (credenciais, { rejectWithValue }) => {
  try {
    const resposta = await servicoAutenticacao.loginCentral(credenciais);
    return resposta;
  } catch (erro: unknown) {
    if (erro instanceof Error) {
      const axiosError = erro as {
        response?: { status?: number; data?: { message?: string; error?: string } };
        code?: string;
        message?: string;
      };

      // Credenciais inválidas
      if (axiosError.response?.status === 401) {
        return rejectWithValue('Credenciais inválidas');
      }

      // Rate limit
      if (axiosError.response?.status === 429) {
        return rejectWithValue('Muitas tentativas. Aguarde antes de tentar novamente.');
      }

      // Timeout
      if (axiosError.code === 'ECONNABORTED' || axiosError.message?.includes('timeout')) {
        return rejectWithValue('Falha de comunicação com o servidor');
      }

      // Erro de rede (sem conexão)
      if (axiosError.code === 'ERR_NETWORK' || axiosError.message?.includes('Network Error')) {
        return rejectWithValue('Sem conexão com o servidor');
      }

      // Mensagem do servidor
      if (axiosError.response?.data?.message) {
        return rejectWithValue(axiosError.response.data.message);
      }
      if (axiosError.response?.data?.error) {
        return rejectWithValue(axiosError.response.data.error);
      }

      return rejectWithValue(erro.message);
    }
    return rejectWithValue('Erro desconhecido ao realizar login.');
  }
});

/**
 * Thunk assíncrono para restaurar sessão a partir do localStorage.
 * Verifica se existe token válido e não expirado no localStorage.
 * Usado na inicialização da aplicação (App mount).
 */
export const restaurarSessao = createAsyncThunk<
  RespostaAutenticacaoCentral,
  void,
  { rejectValue: string }
>('autenticacao/restaurarSessao', async (_, { rejectWithValue }) => {
  try {
    const sessao = servicoAutenticacao.restaurarSessao();
    if (sessao) {
      return sessao;
    }
    return rejectWithValue('Nenhuma sessão válida encontrada.');
  } catch {
    return rejectWithValue('Erro ao restaurar sessão.');
  }
});

/**
 * Thunk assíncrono para realizar logout.
 * Limpa todos os dados de sessão do localStorage e reseta estado.
 */
export const realizarLogout = createAsyncThunk<void, void>(
  'autenticacao/realizarLogout',
  async () => {
    servicoAutenticacao.logout();
  }
);

// ============================================================
// Estado Inicial
// ============================================================

const estadoInicial: EstadoAutenticacao = {
  lojista: null,
  token: null,
  estaAutenticado: false,
  carregando: false,
  erro: null,
  estabelecimentos: [],
};

// ============================================================
// Slice
// ============================================================

const fatiaAutenticacao = createSlice({
  name: 'autenticacao',
  initialState: estadoInicial,
  reducers: {
    limparErro(state) {
      state.erro = null;
    },
    /**
     * Action para marcar sessão como expirada durante sessão ativa.
     * Requisito 6.4: Quando o token expira durante sessão ativa,
     * redirecionar para login com mensagem de sessão expirada.
     */
    sessaoExpirada(state) {
      state.lojista = null;
      state.token = null;
      state.estaAutenticado = false;
      state.carregando = false;
      state.erro = 'Sessão expirada. Faça login novamente.';
      state.estabelecimentos = [];
      servicoAutenticacao.logout();
    },
  },
  extraReducers: (builder) => {
    // realizarLogin
    builder
      .addCase(realizarLogin.pending, (state) => {
        state.carregando = true;
        state.erro = null;
      })
      .addCase(realizarLogin.fulfilled, (state, action) => {
        state.carregando = false;
        state.estaAutenticado = true;
        state.lojista = action.payload.lojista;
        state.token = action.payload.token;
        state.estabelecimentos = action.payload.estabelecimentos;
        state.erro = null;
      })
      .addCase(realizarLogin.rejected, (state, action) => {
        state.carregando = false;
        state.estaAutenticado = false;
        state.lojista = null;
        state.token = null;
        state.estabelecimentos = [];
        state.erro = action.payload ?? 'Erro ao realizar login.';
      });

    // restaurarSessao
    builder
      .addCase(restaurarSessao.pending, (state) => {
        state.carregando = true;
        state.erro = null;
      })
      .addCase(restaurarSessao.fulfilled, (state, action) => {
        state.carregando = false;
        state.estaAutenticado = true;
        state.lojista = action.payload.lojista;
        state.token = action.payload.token;
        state.estabelecimentos = action.payload.estabelecimentos;
        state.erro = null;
      })
      .addCase(restaurarSessao.rejected, (state) => {
        state.carregando = false;
        state.estaAutenticado = false;
        state.lojista = null;
        state.token = null;
        state.estabelecimentos = [];
        // Não define erro — é normal não ter sessão ao iniciar
      });

    // realizarLogout
    builder.addCase(realizarLogout.fulfilled, (state) => {
      state.lojista = null;
      state.token = null;
      state.estaAutenticado = false;
      state.carregando = false;
      state.erro = null;
      state.estabelecimentos = [];
    });
  },
});

export const { limparErro, sessaoExpirada } = fatiaAutenticacao.actions;

export default fatiaAutenticacao.reducer;
